import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/Modal';
import TabBar from '../../components/TabBar';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../utils/api';
import { formatBJTime } from '../../utils/datetime';
import { copyToClipboard } from '../../utils/clipboard';
import {
  ArrowLeft, Video, Send, ChevronDown, ImageOff,
  CheckCircle2, XCircle, RotateCcw, Trash2, Copy, X,
  Download, Loader, Clock,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'assign', label: '指派任务', Icon: Send },
  { key: 'list',   label: '任务列表', Icon: Video },
];

const STATUS_FILTERS = [
  { key: 'all',       label: '全部' },
  { key: 'pending',   label: '待录制' },
  { key: 'submitted', label: '待审核' },
  { key: 'approved',  label: '已通过' },
  { key: 'rejected',  label: '未通过' },
];

const STATUS_BADGE = {
  pending  : { text: '待录制', bg: 'var(--c-bg)',               fg: 'var(--c-text-secondary)', Icon: Clock },
  submitted: { text: '待审核', bg: 'rgba(255,159,10,0.15)',      fg: '#ff9f0a',                  Icon: Loader },
  approved : { text: '已通过', bg: 'rgba(52,199,89,0.15)',       fg: 'var(--c-success)',          Icon: CheckCircle2 },
  rejected : { text: '未通过', bg: 'rgba(255,59,48,0.12)',       fg: 'var(--c-danger)',           Icon: XCircle },
};

// ── 图片灯箱（Portal） ────────────────────────────────────────────────────────
function Lightbox({ url, name, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
        width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <X size={20} color="#fff" />
      </button>
      <img src={url} alt={name || '参考截图'} onClick={e => e.stopPropagation()}
        style={{ maxWidth: '94vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8 }} />
    </div>,
    document.body,
  );
}

// ── 主页面 ────────────────────────────────────────────────────────────────────
export default function VideoReviewPage() {
  const navigate    = useNavigate();
  const { showToast } = useToast();
  const confirm     = useConfirm();
  const { subscribe } = useSocket();

  const [activeTab, setActiveTab] = useState('assign');
  const [newSubmitBadge, setNewSubmitBadge] = useState(0); // 新提交未读数

  // ── 指派任务 ──────────────────────────────────────────────────────────────
  // 不用选用户：输入 11 位号码后自动查这个号码属于哪个业务、哪个用户，确认无误再发送。
  // 同一个号码在排五和证券里都有记录时，列出两个业务让管理员点选一个。
  const [phone,    setPhone]    = useState('');
  const [note,     setNote]     = useState('');
  const [sending,  setSending]  = useState(false);
  const [lookup,   setLookup]   = useState(null);   // { phone, candidates, usable, message } | null
  const [looking,  setLooking]  = useState(false);
  const [pickBiz,  setPickBiz]  = useState('');     // 多个业务时选中的业务
  const lookupSeq = useRef(0);

  const phoneDigits = phone.replace(/\D/g, '').slice(0, 11);
  const phoneValid  = /^1[3-9]\d{9}$/.test(phoneDigits);

  // 号码填满 11 位后自动查归属（防抖，且忽略过期的返回）
  useEffect(() => {
    setLookup(null); setPickBiz('');
    if (!phoneValid) { setLooking(false); return undefined; }
    const seq = ++lookupSeq.current;
    setLooking(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/video-review/lookup', { params: { phone: phoneDigits } });
        if (seq === lookupSeq.current) setLookup(res.data);
      } catch (err) {
        if (seq === lookupSeq.current) setLookup({ phone: phoneDigits, candidates: [], usable: 0, message: err.response?.data?.error || '查询失败，请重试' });
      } finally { if (seq === lookupSeq.current) setLooking(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [phoneDigits, phoneValid]);

  const usable = (lookup?.candidates || []).filter(c => c.ok);
  const target = usable.length === 1 ? usable[0] : usable.find(c => c.business === pickBiz) || null;
  const canSend = !sending && phoneValid && !looking && !!target;

  const handleAssign = async () => {
    if (!phoneValid) return showToast('请输入正确的手机号', 'error');
    if (!target)     return showToast(usable.length > 1 ? '请先选择要推送到哪个业务' : '这个号码没有可推送的用户', 'error');
    setSending(true);
    try {
      const res = await api.post('/video-review/assign', {
        phone: phoneDigits, note: note.trim() || undefined,
        ...(usable.length > 1 ? { business: target.business } : {}),
      });
      const who = `${res.data.business_label}·${res.data.user_name}`;
      showToast(
        res.data.hasRefImage ? `已推送给 ${who}，并自动关联了该号码的截图` : `已推送给 ${who}（未找到关联截图，仅展示号码）`,
        'success',
      );
      setPhone(''); setNote('');
      if (activeTab === 'list') fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '指派失败', 'error');
    } finally { setSending(false); }
  };

  // ── 任务列表 ──────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState('all');
  const [list,         setList]         = useState([]);
  const [loadingList,  setLoadingList]  = useState(false);
  const [lightbox,     setLightbox]     = useState(null); // { url, name }
  const [reviewFor,    setReviewFor]    = useState(null); // { id, action }
  const [reviewNote,   setReviewNote]   = useState('');
  const [actingId,     setActingId]     = useState(null);
  const [expandedRefIds, setExpandedRefIds] = useState(() => new Set()); // 参考截图折叠展开状态

  const toggleRef = (id) => {
    setExpandedRefIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const qs  = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await api.get(`/video-review/admin/list${qs}`);
      setList(res.data.tasks || []);
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoadingList(false); }
  }, [statusFilter, showToast]);

  useEffect(() => { if (activeTab === 'list') fetchList(); }, [activeTab, fetchList]);

  // ── 实时推送：用户提交视频时自动刷新列表 + 角标提示 ──────────────────────
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (event !== 'video_review_submitted') return;
      // 当前在任务列表 Tab 时静默刷新；否则累积角标
      if (activeTab === 'list') {
        fetchList();
        showToast(`号码 ${payload?.phone || ''} 已提交审查视频`, 'success');
      } else {
        setNewSubmitBadge(n => n + 1);
      }
    });
  }, [subscribe, activeTab, fetchList, showToast]);

  const copyPhone = async (p) => {
    await copyToClipboard(p);
    showToast('已复制号码', 'success');
  };

  const doReview = async (id, action, noteText) => {
    setActingId(id);
    try {
      await api.post(`/video-review/admin/${id}/review`, { action, note: noteText || undefined });
      showToast(action === 'approved' ? '已通过' : '已标记未通过', 'success');
      setReviewFor(null); setReviewNote('');
      fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setActingId(null); }
  };

  const doReopen = async (id) => {
    const ok = await confirm('确认打回重录？该任务已提交的视频将被清除，用户会收到重新录制的通知。');
    if (!ok) return;
    setActingId(id);
    try {
      await api.post(`/video-review/admin/${id}/reopen`);
      showToast('已打回，等待用户重新上传', 'success');
      fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setActingId(null); }
  };

  const doDelete = async (id) => {
    const ok = await confirm('确认撤销该任务？记录及关联文件将被删除，此操作不可恢复。', { danger: true });
    if (!ok) return;
    setActingId(id);
    try {
      await api.delete(`/video-review/admin/${id}`);
      showToast('已撤销', 'success');
      fetchList();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setActingId(null); }
  };

  // ── 渲染 ──────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      {lightbox && <Lightbox url={lightbox.url} name={lightbox.name} onClose={() => setLightbox(null)} />}

      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0',
            display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
          <Video size={16} /> 二次审查
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px', paddingBottom: 80 }}>
        {/* 标签栏 */}
        <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--c-bg)', borderRadius: 12, marginBottom: 14 }}>
          {TABS.map(t => {
            const active = t.key === activeTab;
            return (
              <button key={t.key} onClick={() => { setActiveTab(t.key); if (t.key === 'list') setNewSubmitBadge(0); }}
                style={{
                  flex: 1, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: active ? 'var(--c-surface)' : 'transparent',
                  color: active ? 'var(--c-primary)' : 'var(--c-text-secondary)',
                  fontSize: 14, fontWeight: active ? 700 : 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s',
                }}>
                <t.Icon size={15} /> {t.label}
                {/* 新提交角标：只在任务列表 Tab 显示 */}
                {t.key === 'list' && newSubmitBadge > 0 && (
                  <span style={{
                    background: 'var(--c-danger)', color: '#fff',
                    fontSize: 11, fontWeight: 700, lineHeight: '16px',
                    minWidth: 16, height: 16, borderRadius: 8,
                    padding: '0 4px', textAlign: 'center',
                  }}>{newSubmitBadge > 9 ? '9+' : newSubmitBadge}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── 指派任务 ── */}
        {activeTab === 'assign' && (
          <>
            <div className="card">
              {/* 需审查的号码：输入后自动识别归属 */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>需审查的号码</div>
              <input className="input" style={{ marginBottom: 12 }}
                placeholder="输入11位手机号，自动找到对应用户"
                value={phone} onChange={e => setPhone(e.target.value)}
                maxLength={11} inputMode="numeric" />

              {/* 自动识别结果 */}
              {phoneValid && (
                <div style={{ marginBottom: 14 }}>
                  {looking ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--c-text-tertiary)' }}>
                      <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> 正在查找号码归属…
                    </div>
                  ) : lookup && usable.length === 0 ? (
                    <div style={{ background: 'rgba(255,59,48,0.07)', border: '1px solid rgba(255,59,48,0.2)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: 'var(--c-danger)', lineHeight: 1.6 }}>
                      {lookup.message || '没有可推送的用户'}
                    </div>
                  ) : lookup && (
                    <>
                      {usable.length > 1 && (
                        <div style={{ fontSize: 12, color: '#9a6400', marginBottom: 6 }}>这个号码在排五和证券里都有记录，请选择要推送到哪个业务：</div>
                      )}
                      {usable.map(c => {
                        const chosen = usable.length === 1 || pickBiz === c.business;
                        const isPuzzle = c.business === 'puzzle';
                        return (
                          <div key={c.business} onClick={() => usable.length > 1 && setPickBiz(c.business)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 8, borderRadius: 10,
                              cursor: usable.length > 1 ? 'pointer' : 'default',
                              border: `1.5px solid ${chosen ? 'var(--c-primary)' : 'var(--c-border)'}`,
                              background: chosen ? 'var(--c-primary-light)' : 'var(--c-bg)',
                            }}>
                            <span style={{
                              flexShrink: 0, fontSize: 12, fontWeight: 700, borderRadius: 6, padding: '2px 8px',
                              background: isPuzzle ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
                              color: isPuzzle ? '#8944ab' : 'var(--c-primary)',
                            }}>{c.business_label}</span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, wordBreak: 'break-all' }}>
                                {c.user_name}{c.user_phone ? <span style={{ fontWeight: 400, color: 'var(--c-text-secondary)', marginLeft: 4 }}>（{c.user_phone}）</span> : null}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2, wordBreak: 'break-all' }}>
                                {c.detail}{c.via_external ? ` · 外部接单人「${c.via_external}」的号码，推给其负责人` : ''}
                              </div>
                            </div>
                            {usable.length > 1 && (
                              <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${chosen ? 'var(--c-primary)' : 'var(--c-border)'}`, background: chosen ? 'var(--c-primary)' : 'transparent', boxShadow: chosen ? 'inset 0 0 0 3px var(--c-primary-light)' : 'none' }} />
                            )}
                          </div>
                        );
                      })}
                      {/* 有一部分业务查到了、另一部分不能推（越权 / 禁用等）：说明一下，免得管理员以为只有一个业务 */}
                      {(lookup.candidates || []).filter(c => !c.ok).map(c => (
                        <div key={'x' + c.business} style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 4 }}>
                          {c.business_label}：{c.detail ? `${c.detail} · ` : ''}不能推送（{c.reason === 'OUT_OF_SCOPE' ? '不在您名下' : c.reason === 'DISABLED' ? '账号已禁用' : c.reason === 'PACKAGE_UNCLAIMED' ? '数据包还没有人领取' : '没有可推送的负责人'}）
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* 备注 */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>备注说明（可选）</div>
              <textarea className="input"
                style={{ minHeight: 72, resize: 'vertical', fontFamily: 'inherit', fontSize: 14, marginBottom: 16 }}
                placeholder="告知用户录制要求或背景说明…"
                value={note} onChange={e => setNote(e.target.value)} maxLength={200} />

              <button className="btn btn-primary" style={{ width: '100%', height: 46, fontSize: 15, fontWeight: 700, borderRadius: 14 }}
                disabled={!canSend}
                onClick={handleAssign}>
                {sending ? '推送中…' : (target ? `推送给 ${target.user_name}（${target.business_label}）` : '发送二次审查任务')}
              </button>
            </div>

            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--c-text)' }}>使用说明</div>
                <div>① 填写需要录制审查视频的号码，系统自动找到这个号码的用户（排五、证券都能识别），不用手动选人</div>
                <div>② 用户会收到站内通知，标题和内容里写明是「排五」还是「证券」，前往「我的 → 二次审查任务」录制并上传</div>
                <div>③ 上传后状态变为「待审核」，在「任务列表」中下载/审核</div>
                <div>④ 审核不通过可打回重录，用户会收到通知</div>
              </div>
            </div>
          </>
        )}

        {/* ── 任务列表 ── */}
        {activeTab === 'list' && (
          <>
            {/* 状态筛选 */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
              {STATUS_FILTERS.map(f => (
                <button key={f.key} onClick={() => setStatusFilter(f.key)}
                  style={{
                    flexShrink: 0, height: 30, padding: '0 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                    background: statusFilter === f.key ? 'var(--c-primary)' : 'var(--c-bg)',
                    color: statusFilter === f.key ? '#fff' : 'var(--c-text-secondary)',
                  }}>
                  {f.label}
                </button>
              ))}
            </div>

            {loadingList ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--c-text-tertiary)' }}>
                <Loader size={28} style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 8px' }} />
                加载中…
              </div>
            ) : list.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--c-text-tertiary)' }}>
                <Video size={36} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
                <div style={{ fontSize: 15, fontWeight: 600 }}>暂无任务</div>
              </div>
            ) : list.map(task => {
              const badge = STATUS_BADGE[task.status] || STATUS_BADGE.pending;
              const BadgeIcon = badge.Icon;
              const isActing = actingId === task.id;

              return (
                <div key={task.id} className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
                  {/* 卡头 */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px', borderBottom: '1px solid var(--c-divider)',
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.04em' }}>{task.phone}</span>
                        <button onClick={() => copyPhone(task.phone)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                          <Copy size={13} color="var(--c-text-tertiary)" />
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '1px 6px',
                          background: task.business === 'puzzle' ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
                          color: task.business === 'puzzle' ? '#8944ab' : 'var(--c-primary)',
                        }}>{task.business_label || '排五'}</span>
                        <span>{task.user_name || task.user_phone}</span>
                      </div>
                    </div>
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: 11, fontWeight: 600, color: badge.fg,
                      background: badge.bg, borderRadius: 8, padding: '3px 9px', flexShrink: 0,
                    }}>
                      <BadgeIcon size={11} strokeWidth={2.2} /> {badge.text}
                    </span>
                  </div>

                  {/* 参考截图：有图可折叠展开，无图仅显示号码 */}
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--c-divider)' }}>
                    {task.refUrl ? (
                      <>
                        <button onClick={() => toggleRef(task.id)}
                          style={{
                            width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                          <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)' }}>参考截图</span>
                          <ChevronDown size={14} color="var(--c-text-tertiary)"
                            style={{ transform: expandedRefIds.has(task.id) ? 'rotate(180deg)' : 'none', transition: '.18s' }} />
                        </button>
                        {expandedRefIds.has(task.id) && (
                          <img src={task.refUrl} alt="参考截图"
                            onClick={() => setLightbox({ url: task.refUrl, name: task.ref_original_name })}
                            style={{ width: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 8, marginTop: 8,
                              background: 'var(--c-bg)', cursor: 'zoom-in' }} />
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <ImageOff size={13} color="var(--c-text-tertiary)" />
                        未关联参考截图 · 号码 {task.phone}
                      </div>
                    )}
                  </div>

                  {/* 提交视频：直接内嵌预览，无需下载确认 */}
                  {task.videoUrl && (
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--c-divider)' }}>
                      <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 8 }}>提交视频</div>
                      <video controls preload="metadata" src={task.videoUrl}
                        style={{ width: '100%', maxHeight: 220, borderRadius: 8, background: '#000', display: 'block' }} />
                    </div>
                  )}

                  {/* 备注 */}
                  {(task.note || task.review_note) && (
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--c-divider)' }}>
                      {task.note && <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: task.review_note ? 4 : 0 }}>📋 {task.note}</div>}
                      {task.review_note && <div style={{ fontSize: 12, color: task.status === 'rejected' ? 'var(--c-danger)' : 'var(--c-text-secondary)' }}>
                        {task.status === 'rejected' ? '❌ 原因：' : '✅ 批注：'}{task.review_note}
                      </div>}
                    </div>
                  )}

                  {/* 操作区 */}
                  <div style={{ padding: '12px 14px' }}>
                    {/* 已提交 → 审核操作 */}
                    {task.status === 'submitted' && (
                      reviewFor?.id === task.id ? (
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 6 }}>
                            {reviewFor.action === 'rejected' ? '填写未通过原因（可选）：' : '填写审核批注（可选）：'}
                          </div>
                          <input className="input" style={{ marginBottom: 10 }}
                            placeholder={reviewFor.action === 'rejected' ? '未通过原因…' : '审核备注…'}
                            value={reviewNote} onChange={e => setReviewNote(e.target.value)} />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                              onClick={() => { setReviewFor(null); setReviewNote(''); }}>取消</button>
                            <button className="btn btn-sm" disabled={isActing}
                              style={{
                                flex: 2, border: 'none', color: '#fff', fontWeight: 700,
                                background: reviewFor.action === 'approved' ? 'var(--c-success)' : 'var(--c-danger)',
                              }}
                              onClick={() => doReview(task.id, reviewFor.action, reviewNote)}>
                              {isActing ? '处理中…' : reviewFor.action === 'approved' ? '确认通过' : '确认未通过'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                          {task.videoUrl && (
                            <a href={task.videoUrl} download
                              style={{ textDecoration: 'none', flex: 1 }}>
                              <button className="btn btn-outline btn-sm" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                                <Download size={13} /> 下载视频
                              </button>
                            </a>
                          )}
                          <button className="btn btn-sm" disabled={isActing}
                            style={{ flex: 1, background: 'var(--c-success)', border: 'none', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                            onClick={() => { setReviewFor({ id: task.id, action: 'approved' }); setReviewNote(''); }}>
                            <CheckCircle2 size={13} /> 通过
                          </button>
                          <button className="btn btn-sm" disabled={isActing}
                            style={{ flex: 1, background: 'var(--c-danger)', border: 'none', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                            onClick={() => { setReviewFor({ id: task.id, action: 'rejected' }); setReviewNote(''); }}>
                            <XCircle size={13} /> 未通过
                          </button>
                        </div>
                      )
                    )}

                    {/* 已通过 / 已拒绝 → 可下载 + 打回 */}
                    {['approved', 'rejected'].includes(task.status) && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        {task.videoUrl && (
                          <a href={task.videoUrl} download style={{ textDecoration: 'none', flex: 1 }}>
                            <button className="btn btn-outline btn-sm" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                              <Download size={13} /> 下载视频
                            </button>
                          </a>
                        )}
                        <button className="btn btn-outline btn-sm" disabled={isActing}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                          onClick={() => doReopen(task.id)}>
                          <RotateCcw size={13} /> 打回重录
                        </button>
                      </div>
                    )}

                    {/* 待录制 → 只显示等待提示 */}
                    {task.status === 'pending' && (
                      <div style={{ fontSize: 13, color: 'var(--c-text-tertiary)', marginBottom: 10 }}>
                        等待用户录制并上传…
                      </div>
                    )}

                    {/* 底栏：时间 + 撤销 */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>
                        指派 {formatBJTime(task.created_at)}
                        {task.submitted_at ? ` · 提交 ${formatBJTime(task.submitted_at)}` : ''}
                      </span>
                      <button onClick={() => doDelete(task.id)} disabled={isActing}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                          display: 'flex', alignItems: 'center', color: 'var(--c-text-tertiary)' }}
                        title="撤销任务">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
